import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin, MessageSquareText, Clock, Users, Lock, ListChecks, Share2, Inbox, IdCard } from "lucide-react";
import { toast } from "sonner";

import { own } from "@/lib/memberDerive";
import { writeOutcome } from "@/lib/community";
import { PASS_SECTIONS, PASS_GROUPS, passSection, normalizePass, emptySection, sectionEntries, sectionSummary, sectionPayload, ownPass, presentationCode, dayOnly, dayTime, NOTE_MAX } from "@/lib/v3Pass";
import { useCore } from "../useCore";
import { mutate } from "../actions";
import { ConnectionError } from "../components/ConnectionError";
import { Loading, Unavailable, PrivacyNote, ErrorNote, CardLink } from "../components/kit";
import { V3Title, V3Columns, V3Field, V3Actions, StatusBanner, V3_PRIMARY, V3_SECONDARY } from "../components/v3";
import { V3Select, AreaCard, ResponsibleCard, SectionTitle, V3_CARD, V3_LINK, V3_SMALL } from "../components/v3app";
import { PrivateBanner, PassGroupCard, QrCode, useExpired } from "../components/v3pass";

const GROUP_ICON = { mobilitaet: MapPin, kommunikation: MessageSquareText, "ruhe-tempo": Clock, "begleitung-anreise": Users };
const MISSING = "Der gemeinsame Core kann Access-Pass-Angaben noch nicht laden oder speichern (GET /member/access-pass fehlt). Du kannst alle Bereiche ansehen; gespeichert oder geteilt wird nichts.";

// GET /member/access-pass (contract C.7); 404 = not yet in the Core.
export const usePass = () => useCore("/member/access-pass", (d) => ({ pass: normalizePass(d) }));
export const passState = (res) => (res.error === "notfound" ? "missing" : res.error ? "error" : !res.data ? "loading" : !res.data.pass ? "format" : "ok");

export function PassStateNote({ state, onRetry }) {
  if (state === "missing") return <StatusBanner tone="notice" icon={Lock} title="Gespeicherte Angaben noch nicht verfügbar" text={MISSING} testid="access-pass-unavailable" />;
  if (state === "format") return <div className="mb-[17px]"><Unavailable testid="access-pass-format">Die Antwort des Servers hat ein unerwartetes Format. Es wird nichts angezeigt und nichts gespeichert.</Unavailable></div>;
  if (state === "error") return <div className="mb-[17px] space-y-2"><ErrorNote testid="access-pass-error">Deine Angaben konnten nicht geladen werden.</ErrorNote><button type="button" onClick={onRetry} className={V3_SMALL}>Erneut laden</button></div>;
  if (state === "loading") return <p className="mb-[17px] text-[14px] text-v3-muted" role="status">Gespeicherte Angaben werden geladen …</p>;
  return null;
}

function SectionSummary({ section, data, state }) {
  const rows = state === "ok" ? sectionEntries(section, data) : [];
  return (
    <div data-testid={`access-pass-summary-${section.key}`}>
      <p className="text-[15px] font-bold text-v3-ink">{section.title}</p>
      {state !== "ok" ? <p className="mt-[4px] text-[14px] text-v3-muted">Gespeicherte Angaben: nicht verfügbar</p>
        : rows.length ? <ul className="mt-[4px] space-y-[2px] text-[14px] text-v3-muted">{rows.map(([l, v]) => <li key={l} className="break-words"><span className="font-bold text-v3-ink">{l}:</span> {v}</li>)}</ul>
        : <p className="mt-[4px] text-[14px] text-v3-muted">Noch keine Angaben gespeichert.</p>}
      <Link to={`/access-pass/${section.key}`} data-testid={`access-pass-open-${section.key}`} className={`${V3_LINK} text-[14px]`}>{section.title} bearbeiten</Link>
    </div>
  );
}

function PresentationCode({ code }) {
  const [shown, setShown] = useState(false);
  const expired = useExpired(code.expiresAt);
  if (expired) return <p className="text-[14px] text-v3-muted" data-testid="pass-code-expired">Der Vorzeigecode ist abgelaufen. Lade die Seite neu, um einen neuen zu erhalten.</p>;
  if (!shown) return <button type="button" onClick={() => setShown(true)} data-testid="pass-code-show" className={V3_SMALL}>Vorzeigecode anzeigen</button>;
  return (
    <div className="flex flex-col gap-[14px] sm:flex-row sm:items-center" data-testid="pass-code">
      {code.rows ? <QrCode rows={code.rows} label={`QR-Code für den Vorzeigecode ${code.code}`} /> : <p className="text-[14px] text-v3-muted" data-testid="pass-qr-missing">QR-Code nicht verfügbar – nutze den Code als Text.</p>}
      <div>
        <p className="text-[13px] font-bold text-v3-muted">Vorzeigecode als Text</p>
        <p className="mt-[4px] font-mono text-[22px] font-bold tracking-[0.08em] text-v3-ink" data-testid="pass-code-text">{code.code}</p>
        <p className="mt-[4px] text-[13px] text-v3-muted">Gültig bis {dayTime(code.expiresAt)} · kurzlebig, enthält keine Ausweisnummer</p>
        <button type="button" onClick={() => setShown(false)} data-testid="pass-code-hide" className={`${V3_LINK} text-[14px]`}>Code ausblenden</button>
      </div>
    </div>
  );
}

// Own card data from GET /member/profile; the QR only shows a Core-issued, short-lived code (never the pass number).
function MemberCard() {
  const res = useCore("/member/profile");
  if (!res.data) return null;
  const card = ownPass(res.data);
  const code = presentationCode(res.data);
  return (
    <section aria-labelledby="pass-card-title" className={`${V3_CARD} mt-[22px] px-[20px] py-[18px]`} data-testid="member-pass-card">
      <h2 id="pass-card-title" className="flex items-center gap-[12px] text-[17px] font-bold text-v3-ink"><IdCard className="h-[18px] w-[18px] text-v3-magenta" aria-hidden="true" />Mitgliedsausweis</h2>
      <p className="mt-[8px] text-[14px] text-v3-muted" data-testid="member-pass-number">{card ? `Ausweisnummer ${card.number || "nicht angegeben"} · gültig bis ${card.validUntil ? dayOnly(card.validUntil) : "nicht angegeben"}` : "Kein Ausweis hinterlegt."}</p>
      {card && <div className="mt-[14px]">{code ? <PresentationCode code={code} />
        : <p className="text-[14px] text-v3-muted" data-testid="pass-code-unavailable"><span className="font-bold text-v3-ink">Vorzeigecode (QR): noch nicht verfügbar.</span> Der gemeinsame Core stellt keinen kurzlebigen Vorzeigecode bereit. Die Ausweisnummer wird bewusst nicht als QR-Code ausgegeben.</p>}</div>}
    </section>
  );
}

// M19: four areas as in the template; values only from the Core, nothing kept locally.
export default function AccessPass() {
  const res = usePass();
  const [open, setOpen] = useState(null);
  const state = passState(res);
  const pass = state === "ok" ? res.data.pass : null;
  return (
    <div data-testid="access-pass" data-m="M19">
      <V3Title title="Mein Access Pass und Bearbeiten" intro="Dein freiwilliger Access Pass ist zunächst nur für dich sichtbar." testid="access-pass-title" />
      <V3Columns layout="detail" aside={<><AreaCard title="Dein Bereich" text="Eigene Präferenzen bearbeiten" testid="access-pass-area" /><ResponsibleCard /></>} main={<>
        <PrivateBanner text="Du entscheidest, welche Angaben du speichern und später gezielt teilen möchtest." />
        <PassStateNote state={state} onRetry={res.reload} />
        <div className="space-y-[13px]">
          {PASS_GROUPS.map((g) => (
            <PassGroupCard key={g.key} icon={GROUP_ICON[g.key]} title={g.title} subtitle="Eigene Präferenzen bearbeiten" open={open === g.key} onToggle={() => setOpen(open === g.key ? null : g.key)} testid={`access-pass-group-${g.key}`}>
              {g.sections.map((k) => <SectionSummary key={k} section={passSection(k)} data={pass?.[k]} state={state} />)}
            </PassGroupCard>
          ))}
        </div>
        <V3Actions><Link to={`/access-pass/${PASS_SECTIONS[0].key}`} data-testid="access-pass-edit" className={V3_PRIMARY}>Angaben bearbeiten</Link></V3Actions>
        <section aria-labelledby="pass-shares-title" className="mt-[30px]">
          <SectionTitle id="pass-shares-title">Freigaben</SectionTitle>
          <div className="mt-[12px] grid gap-[11px] sm:grid-cols-3">
            <CardLink to="/access-pass/freigaben" icon={ListChecks} title="Freigaben im Überblick" text="Aktive, beendete und angefragte Freigaben" testid="access-pass-grants-link" />
            <CardLink to="/access-pass/teilen" icon={Share2} title="Gezielt teilen" text="Person, Zweck, Angaben und Dauer wählen" testid="access-pass-share-link" />
            <CardLink to="/access-pass/empfangen" icon={Inbox} title="Mit mir geteilt" text="Angaben, die andere mit dir teilen" testid="access-pass-received-link" />
          </div>
        </section>
        <MemberCard />
        <div className="mt-[18px]"><PrivacyNote>Kontaktannahme, Gruppenbeitritt und Teilnahme geben deinen Pass niemals automatisch frei.</PrivacyNote></div>
      </>} />
    </div>
  );
}

function SectionForm({ s, initial, available, state, onRetry }) {
  const [form, setForm] = useState({ values: { ...initial.values }, note: initial.note });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const noteTooLong = Array.from(form.note).length > NOTE_MAX;
  const setValue = (k) => (e) => { const v = e.target.value; setForm((f) => ({ ...f, values: { ...f.values, [k]: v } })); setError(""); };
  // PUT replaces the whole section, so repeating after an unclear answer cannot create duplicates.
  const save = async () => {
    const { error: er, body } = sectionPayload(s, form);
    if (er) { setError(er); return; }
    setBusy(true); setError("");
    const r = await mutate("put", `/member/access-pass/sections/${s.core}`, body, "Die Angaben wurden nicht gespeichert");
    setBusy(false);
    const saved = own(r.data, "section");
    const o = writeOutcome(r, saved && typeof saved === "object" ? saved : null, "Ob die Angaben gespeichert wurden, ist unklar. Bitte lade die Seite neu und prüfe den Stand.");
    if (o.state !== "saved") { setError(o.error); return; }
    const next = normalizePass({ sections: { [s.core]: saved } })[s.key];
    setForm({ values: next.values, note: next.note });
    toast.success("Angaben gespeichert.");
  };
  return (
    <div data-testid="access-pass-section" data-m={s.m}>
      <V3Title title={`Access Pass: ${s.title}`} intro={s.intro} testid="access-pass-section-title" />
      <V3Columns layout="detail" aside={<><AreaCard title="Dein Bereich" text={sectionSummary(s, form)} testid="access-pass-section-area" /><ResponsibleCard /></>} main={<>
        <PrivateBanner text="Du kannst jedes Feld auslassen und später gezielt für eine Person freigeben." />
        <PassStateNote state={state} onRetry={onRetry} />
        {s.fields.map(([k, label, options]) => (
          <V3Select key={k} id={`ap-${s.key}-${k}`} label={label} value={form.values[k] || ""} onChange={setValue(k)} testid={`ap-${s.key}-${k}`}>
            <option value="">Keine Angabe</option>
            {options.map(([o, l]) => <option key={o} value={o}>{l}</option>)}
          </V3Select>
        ))}
        {s.note && <V3Field id={`ap-${s.key}-note`} label={s.note[0]} placeholder={s.note[1]} value={form.note} onChange={(e) => { setForm((f) => ({ ...f, note: e.target.value })); setError(""); }}
          error={noteTooLong ? `Höchstens ${NOTE_MAX} Zeichen.` : ""} hint="Freiwillig. Keine Diagnosen, keine Wohnadresse, Namen anderer nur mit deren Zustimmung." />}
        {error && <div className="mb-[12px]"><ErrorNote testid="access-pass-section-error">{error}</ErrorNote></div>}
        {!available && <p className="text-[14px] text-v3-muted" data-testid="access-pass-section-blocked">Speichern ist gesperrt: Deine Auswahl bleibt nur auf dieser Seite und geht beim Verlassen verloren.</p>}
        <V3Actions>
          <button type="button" onClick={save} disabled={!available || busy || noteTooLong} data-testid="access-pass-section-save" className={V3_PRIMARY}>{busy ? "Wird gespeichert …" : "Angaben speichern"}</button>
          <Link to="/access-pass" data-testid="access-pass-section-back" className={V3_SECONDARY}>Zurück</Link>
        </V3Actions>
        <nav aria-label="Weitere Bereiche" className="mt-[22px] flex flex-wrap gap-x-[18px]">
          {PASS_SECTIONS.filter((x) => x.key !== s.key).map((x) => <Link key={x.key} to={`/access-pass/${x.key}`} className={`${V3_LINK} text-[14px]`} data-testid={`access-pass-nav-${x.key}`}>{x.title}</Link>)}
        </nav>
      </>} />
    </div>
  );
}

// M84–M89: one section; saving only when the Core delivers the pass (PUT …/sections/{key}, contract C.7).
export function AccessPassSection() {
  const { section } = useParams();
  const s = passSection(section);
  const res = usePass();
  const state = passState(res);
  if (!s) return (<><V3Title title="Bereich nicht gefunden" testid="access-pass-section-title" /><Link to="/access-pass" className={V3_LINK} data-testid="access-pass-section-missing">Zum Access Pass</Link></>);
  if (state === "loading") return <Loading />;
  if (state === "error") return <ConnectionError onRetry={res.reload} />;
  const available = state === "ok";
  return <SectionForm key={`${s.key}:${state}`} s={s} initial={available ? res.data.pass[s.key] : emptySection()} available={available} state={state} onRetry={res.reload} />;
}
